import { OrderHistory } from '../../stores/appOrderHistoryStore';
import { RevenueHistory } from '../../services/revenuehistory/revenueHistoryService';
import { EnrichedCostPriceAnalysis } from '../../services/costPrice/costPriceService';
import { CostPriceCategory } from '../../services/costPrice/costPriceCategoriesService';
import { getItemTotalRevenue } from '../revenueAddOns';
import { calculateProductTotal } from '../platformUtils';
import { filterRevenueByOverallType } from '../../components/revenuehistory/utils';
import { filterOrdersByOverallType } from '../../components/orderhistory/utils';
import { getEffectiveMenuPrice, calculateCostPercentage, COST_PRICE_TYPE_CHOICE } from '../costPriceUtils';
import { ReportRange, inRange } from './shared';

/**
 * Pure, React-free computation for the Cost Price & Margin report (R3). The
 * cost-price page recast as a period performance report, sales-weighted.
 *
 * **Window rule (pinned, mirrors the cost-price page):** dish cost and menu price
 * come from the CURRENT enrichment (latest ingredient prices; BYO choice-mix
 * weighted over its built-in trailing 2-month window) — the caller enriches the
 * full all-time lists exactly like `CostPricePageNew.tsx` and passes the result
 * in. Qty sold, revenue and margin € contribution are scoped to the SELECTED
 * report period. Dish costs are NEVER recomputed here.
 *
 * `showExVat` mirrors the page's `showExVatOrders` toggle so dish cost / menu
 * price / cost % match the cost-price page 1:1. Period revenue stays on the
 * ex-VAT basis (`getItemTotalRevenue(item, true)`) per the report parity rules,
 * matching the Period Overview report.
 */

/** Margin threshold for the BCG quadrant Y-axis (72%). */
export const QUADRANT_MARGIN_THRESHOLD = 72;

/** Minimum analysed dishes before the quadrant section is shown. */
const MIN_DISHES_FOR_QUADRANTS = 2;

export interface CostPriceDishRow {
  id: string;
  name: string;
  /** Current enriched dish cost (latest prices, BYO-weighted). */
  cost: number;
  /** Effective menu price in the active VAT basis. */
  menuPrice: number;
  costPercentage: number;
  marginPercentage: number;
  /** Units sold in the selected period (exact name match on `menuItemName`). */
  quantitySold: number;
  /** Ex-VAT revenue in the selected period. */
  revenue: number;
  /** (menuPrice − cost) × quantitySold. */
  marginContribution: number;
}

export interface CostPriceCategoryGroup {
  id: string;
  name: string;
  isDefault: boolean;
  dishes: CostPriceDishRow[];
  totalQuantitySold: number;
  totalRevenue: number;
  totalMarginContribution: number;
}

/** One side (kitchen/bar/total) of the theoretical-vs-actual gap. */
export interface GapBreakdown {
  revenue: number;
  actualCost: number;
  /** Quantity-weighted theoretical cost % (mirrors the charts page). */
  theoreticalPct: number;
  actualPct: number;
  /** actualPct − theoreticalPct, in percentage points. */
  gapPp: number;
}

export interface TheoreticalVsActual {
  total: GapBreakdown;
  kitchen: GapBreakdown;
  bar: GapBreakdown;
  /** Distinct menu items sold in the period that have a cost price. */
  dishesWithRecipeSold: number;
  /** Distinct menu items sold in the period with NO cost price. */
  dishesWithoutRecipeSold: number;
}

export interface CoverageRow {
  name: string;
  quantity: number;
  revenue: number;
}

export type Quadrant = 'Star' | 'Plowhorse' | 'Puzzle' | 'Dog';

export interface QuadrantRow {
  id: string;
  name: string;
  quantitySold: number;
  marginPercentage: number;
  quadrant: Quadrant;
}

export interface QuadrantSection {
  rows: QuadrantRow[];
  /** Average qty sold across analysed dishes — the X-axis threshold. */
  avgQuantity: number;
  /** The Y-axis margin threshold (%). */
  marginThreshold: number;
  /** False when fewer than the minimum analysable dishes exist. */
  available: boolean;
}

export interface CostPriceReportData {
  categories: CostPriceCategoryGroup[];
  theoreticalVsActual: TheoreticalVsActual;
  coverage: CoverageRow[];
  quadrants: QuadrantSection;
  /** False when the restaurant has no cost prices at all (empty-state hint). */
  hasCostPrices: boolean;
  comparisonHidden: boolean;
}

export interface CostPriceReportInputs {
  /** All-time enriched cost-price analyses (enriched by the caller like the page). */
  enriched: EnrichedCostPriceAnalysis[];
  /** All-time revenue (used both for current menu prices and period sales). */
  revenue: RevenueHistory[];
  /** All-time orders (period-filtered here for actual spend). */
  orders: OrderHistory[];
  categories: CostPriceCategory[];
  /** Mirrors the page's `showExVatOrders` toggle. */
  showExVat: boolean;
  range: ReportRange;
}

interface PeriodSales {
  quantity: number;
  revenue: number;
}

/** Aggregate units sold + ex-VAT revenue per menu-item name within the period. */
function buildPeriodSalesByName(revenueInRange: RevenueHistory[]): Map<string, PeriodSales> {
  const sales = new Map<string, PeriodSales>();
  revenueInRange.forEach((rev) => {
    rev.revenueData.categories.forEach((category) => {
      category.items.forEach((item) => {
        if (!item.name) return;
        const agg = sales.get(item.name) || { quantity: 0, revenue: 0 };
        agg.quantity += item.quantity || 0;
        agg.revenue += getItemTotalRevenue(item, true);
        sales.set(item.name, agg);
      });
    });
  });
  return sales;
}

/** Ex-VAT order spend for one side, mirroring the snapshot's per-side cost. */
function sideOrderSpend(orders: OrderHistory[], side: 'food' | 'drinks', showExVat: boolean): number {
  return filterOrdersByOverallType(orders, side).reduce(
    (sum, order) =>
      sum + order.productData.reduce((ps, product) => ps + calculateProductTotal(product, order.platform, showExVat), 0),
    0
  );
}

/** Ex-VAT revenue for one side within the period. */
function sideRevenue(revenue: RevenueHistory[], side: 'food' | 'drinks'): number {
  const filtered: RevenueHistory[] = filterRevenueByOverallType(revenue, side);
  return filtered.reduce((sum, rev) => {
    let total = 0;
    rev.revenueData.categories.forEach((category) => {
      category.items.forEach((item) => {
        total += getItemTotalRevenue(item, true);
      });
    });
    return sum + total;
  }, 0);
}

/** Quantity-weighted theoretical cost % for one side, mirroring the charts page
 * `calculateMenuCostVsActual`: Σ(dish cost% × qty) / Σ(qty) over period revenue
 * items that match a cost price by exact menu-item name. Returns the running
 * numerator/denominator so the total can combine both sides on the same basis. */
function sideWeighted(
  revenue: RevenueHistory[],
  side: 'food' | 'drinks',
  costPctByMenuName: Map<string, number>
): { num: number; den: number } {
  const filtered: RevenueHistory[] = filterRevenueByOverallType(revenue, side);
  let num = 0;
  let den = 0;
  filtered.forEach((rev) => {
    rev.revenueData.categories.forEach((category) => {
      category.items.forEach((item) => {
        const costPct = item.name ? costPctByMenuName.get(item.name) : undefined;
        const qty = item.quantity || 0;
        if (costPct !== undefined && qty > 0) {
          num += costPct * qty;
          den += qty;
        }
      });
    });
  });
  return { num, den };
}

/** Collapse a running numerator/denominator into a quantity-weighted %. */
function weightedPct(w: { num: number; den: number }): number {
  return w.den > 0 ? w.num / w.den : 0;
}

function gap(revenue: number, theoreticalPct: number, actualCost: number): GapBreakdown {
  const actualPct = revenue > 0 ? (actualCost / revenue) * 100 : 0;
  return {
    revenue,
    actualCost,
    theoreticalPct,
    actualPct,
    gapPp: actualPct - theoreticalPct,
  };
}

export function computeCostPriceReport(inputs: CostPriceReportInputs): CostPriceReportData {
  const { enriched, revenue, orders, categories, showExVat, range } = inputs;

  const revenueInRange = revenue.filter((r) => inRange(new Date(r.date), range));
  const ordersInRange = orders.filter((o) => inRange(new Date(o.orderDate), range));
  const periodSales = buildPeriodSalesByName(revenueInRange);

  // Non-CHOICE analyses only — CHOICE rows are shared per-pick costs, never dishes.
  const dishAnalyses = enriched.filter((a) => a.costPriceType !== COST_PRICE_TYPE_CHOICE);

  // --- per-dish rows (cost/menu price/cost%/margin% match the cost-price page) ---
  const dishes: CostPriceDishRow[] = [];
  // Dish id → its category id (for grouping below), kept off the row itself.
  const categoryIdByDish = new Map<string, string | undefined>();
  // Menu-item name → dish cost % (same basis as the rows below), used for the
  // quantity-weighted theoretical gap that mirrors the charts page. Only dishes
  // with a valid cost and menu price are included, exactly like the charts calc.
  const costPctByMenuName = new Map<string, number>();
  dishAnalyses.forEach((analysis) => {
    const effective = getEffectiveMenuPrice(analysis, revenue);
    const menuPrice = effective ? (showExVat ? effective.menuExVatPrice : effective.menuPrice) : 0;
    const cost = analysis.totalCost;
    const costPercentage = calculateCostPercentage(cost, menuPrice);
    if (analysis.menuItemName && cost > 0 && menuPrice > 0) {
      costPctByMenuName.set(analysis.menuItemName, costPercentage);
    }
    const marginPercentage = menuPrice > 0 ? ((menuPrice - cost) / menuPrice) * 100 : 0;
    const sales = analysis.menuItemName ? periodSales.get(analysis.menuItemName) : undefined;
    const quantitySold = sales?.quantity ?? 0;
    const periodRevenue = sales?.revenue ?? 0;
    dishes.push({
      id: analysis.id,
      name: analysis.costPriceName,
      cost,
      menuPrice,
      costPercentage,
      marginPercentage,
      quantitySold,
      revenue: periodRevenue,
      marginContribution: (menuPrice - cost) * quantitySold,
    });
    categoryIdByDish.set(analysis.id, analysis.categoryId);
  });

  // --- per category (scope 'revenue'/undefined only; Uncategorised sorted last) ---
  const revenueCategories = categories
    .filter((c) => (c.scope ?? 'revenue') === 'revenue')
    .sort((a, b) => {
      if (!!a.isDefault !== !!b.isDefault) return a.isDefault ? 1 : -1;
      return a.position - b.position;
    });
  const defaultCategory = revenueCategories.find((c) => c.isDefault);
  const categoryIds = new Set(revenueCategories.map((c) => c.id));

  const dishesByCategory = new Map<string, CostPriceDishRow[]>();
  dishes.forEach((dish) => {
    // Dishes with no/unknown category fall into the default ("Uncategorised").
    const dishCategoryId = categoryIdByDish.get(dish.id);
    const catId = dishCategoryId && categoryIds.has(dishCategoryId) ? dishCategoryId : defaultCategory?.id;
    if (!catId) return;
    const list = dishesByCategory.get(catId) || [];
    list.push(dish);
    dishesByCategory.set(catId, list);
  });

  const categoryGroups: CostPriceCategoryGroup[] = revenueCategories
    .map((category) => {
      const list = (dishesByCategory.get(category.id) || []).sort((a, b) => b.revenue - a.revenue);
      return {
        id: category.id,
        name: category.name,
        isDefault: !!category.isDefault,
        dishes: list,
        totalQuantitySold: list.reduce((s, d) => s + d.quantitySold, 0),
        totalRevenue: list.reduce((s, d) => s + d.revenue, 0),
        totalMarginContribution: list.reduce((s, d) => s + d.marginContribution, 0),
      };
    })
    .filter((group) => group.dishes.length > 0);

  // --- theoretical vs actual gap (per side + total) ---
  const costByMenuName = new Map<string, number>();
  dishAnalyses.forEach((a) => {
    if (a.menuItemName) costByMenuName.set(a.menuItemName, a.totalCost);
  });

  const kitchenRevenue = sideRevenue(revenueInRange, 'food');
  const barRevenue = sideRevenue(revenueInRange, 'drinks');
  const kitchenW = sideWeighted(revenueInRange, 'food', costPctByMenuName);
  const barW = sideWeighted(revenueInRange, 'drinks', costPctByMenuName);
  const totalW = { num: kitchenW.num + barW.num, den: kitchenW.den + barW.den };
  const kitchenActual = sideOrderSpend(ordersInRange, 'food', showExVat);
  const barActual = sideOrderSpend(ordersInRange, 'drinks', showExVat);

  // Distinct sold names with/without a matching cost price.
  let dishesWithRecipeSold = 0;
  let dishesWithoutRecipeSold = 0;
  periodSales.forEach((_sales, name) => {
    if (costByMenuName.has(name)) dishesWithRecipeSold += 1;
    else dishesWithoutRecipeSold += 1;
  });

  const theoreticalVsActual: TheoreticalVsActual = {
    kitchen: gap(kitchenRevenue, weightedPct(kitchenW), kitchenActual),
    bar: gap(barRevenue, weightedPct(barW), barActual),
    total: gap(kitchenRevenue + barRevenue, weightedPct(totalW), kitchenActual + barActual),
    dishesWithRecipeSold,
    dishesWithoutRecipeSold,
  };

  // --- coverage: sold items with no cost price, ranked by period revenue ---
  const coverage: CoverageRow[] = Array.from(periodSales.entries())
    .filter(([name]) => !costByMenuName.has(name))
    .map(([name, s]) => ({ name, quantity: s.quantity, revenue: s.revenue }))
    .sort((a, b) => b.revenue - a.revenue);

  // --- quadrants (BCG menu-engineering matrix) ---
  const analysable = dishes.filter((d) => d.quantitySold > 0 && d.cost > 0 && d.menuPrice > 0);
  const avgQuantity =
    analysable.length > 0
      ? analysable.reduce((s, d) => s + d.quantitySold, 0) / analysable.length
      : 0;
  const quadrantAvailable = analysable.length >= MIN_DISHES_FOR_QUADRANTS;
  const quadrantRows: QuadrantRow[] = quadrantAvailable
    ? analysable.map((d) => {
        const highMargin = d.marginPercentage >= QUADRANT_MARGIN_THRESHOLD;
        const highVolume = d.quantitySold >= avgQuantity;
        const quadrant: Quadrant =
          highMargin && highVolume
            ? 'Star'
            : highMargin && !highVolume
            ? 'Puzzle'
            : !highMargin && highVolume
            ? 'Plowhorse'
            : 'Dog';
        return {
          id: d.id,
          name: d.name,
          quantitySold: d.quantitySold,
          marginPercentage: d.marginPercentage,
          quadrant,
        };
      })
    : [];

  return {
    categories: categoryGroups,
    theoreticalVsActual,
    coverage,
    quadrants: {
      rows: quadrantRows,
      avgQuantity,
      marginThreshold: QUADRANT_MARGIN_THRESHOLD,
      available: quadrantAvailable,
    },
    hasCostPrices: dishAnalyses.length > 0,
    comparisonHidden: range.comparisonHidden,
  };
}
